import { CalendarSchedulingSettings } from '@cue/api';
import { translation } from '@cue/translations';
import { formatDurationMinutesForDisplay } from '@assist/shared/data';

type SchedulingRulesTranslate = (key: string, params?: Record<string, number | string>) => string;

export function formatSchedulingRulesForDisplay(
  settings: CalendarSchedulingSettings | null | undefined,
  translate: SchedulingRulesTranslate,
  options: {
    recurringMaxAdvanceAsOccurrences: boolean;
  }
): string[] {
  if (!settings) {
    return [];
  }

  const rules: string[] = [];

  if (settings.minimumMeetingDurationInMinutes != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleMinDuration, {
        duration: formatDurationMinutesForDisplay(settings.minimumMeetingDurationInMinutes, translate),
      })
    );
  }

  if (settings.maximumMeetingDurationInMinutes != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleMaxDuration, {
        duration: formatDurationMinutesForDisplay(settings.maximumMeetingDurationInMinutes, translate),
      })
    );
  }

  if (settings.maxAdvanceDays != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleMaxAdvanceBooking, {
        days: settings.maxAdvanceDays,
      })
    );
  }

  if (settings.recurringMaxAdvanceDays != null) {
    rules.push(
      options.recurringMaxAdvanceAsOccurrences
        ? translate(translation.reservationPopup.schedulingRuleRecurringMaxOccurrences, {
            occurrences: settings.recurringMaxAdvanceDays,
          })
        : translate(translation.reservationPopup.schedulingRuleRecurringMaxAdvanceBooking, {
            days: settings.recurringMaxAdvanceDays,
          })
    );
  }

  if (settings.rejectOutsideWorkingHours) {
    rules.push(translate(translation.reservationPopup.schedulingRuleOutsideWorkingHoursRejected));
  }

  if (settings.allowRecurringMeetings != null) {
    rules.push(
      translate(
        settings.allowRecurringMeetings
          ? translation.reservationPopup.schedulingRuleRecurringMeetingsAllowed
          : translation.reservationPopup.schedulingRuleRecurringMeetingsNotAllowed
      )
    );
  }

  if (settings.bookingWindowInDays != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleRecurringBookingWindow, {
        days: settings.bookingWindowInDays,
      })
    );
  }

  if (settings.enforceSchedulingHorizon != null) {
    rules.push(
      translate(
        settings.enforceSchedulingHorizon
          ? translation.reservationPopup.schedulingRuleRecurringHorizonEnforcementEnabled
          : translation.reservationPopup.schedulingRuleRecurringHorizonEnforcementDisabled
      )
    );
  }

  if (settings.allowConflicts != null) {
    rules.push(
      translate(
        settings.allowConflicts
          ? translation.reservationPopup.schedulingRuleConflictsAllowed
          : translation.reservationPopup.schedulingRuleConflictsNotAllowed
      )
    );
  } else if (settings.allowRecurringConflicts != null) {
    rules.push(
      translate(
        settings.allowRecurringConflicts
          ? translation.reservationPopup.schedulingRuleConflictsAllowed
          : translation.reservationPopup.schedulingRuleConflictsNotAllowed
      )
    );
  }

  if (settings.maximumConflictInstances != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleMaxConflictInstances, {
        count: settings.maximumConflictInstances,
      })
    );
  }

  if (settings.conflictPercentageAllowed != null) {
    rules.push(
      translate(translation.reservationPopup.schedulingRuleMaxConflictPercentage, {
        percentage: settings.conflictPercentageAllowed,
      })
    );
  }

  return rules;
}
